class CardVariantSwitcher {
  constructor() {
    this.imageCache = new Map();
    this.init();
    this.preloadImages();
    this.setupDynamicContentListener();
  }

  setupDynamicContentListener() {
    document.addEventListener('product-recommendations-loaded', (e) => {
      this.preloadImages();
    });
  }

  init() {
    // Use event delegation for better performance
    document.addEventListener('click', (e) => {
      const swatch = e.target?.closest('.card__color-swatch');
      if (!swatch) return;

      e.preventDefault();
      e.stopPropagation();
      
      const card = swatch.closest('.card-wrapper');
      if (!card) return;

      // Skip if already active
      if (swatch.classList.contains('active')) return;

      const variantId = swatch.dataset.variantId;
      const variantImage = swatch.dataset.variantImage;
      const variantImageWidth = swatch.dataset.variantImageWidth;
      const variantImageHeight = swatch.dataset.variantImageHeight;
      const variantImageAlt = swatch.dataset.variantImageAlt;

      if (!variantImage) return;

      // Update everything synchronously for instant response
      requestAnimationFrame(() => {
        this.updateCardImage(card, {
          url: variantImage,
          width: variantImageWidth,
          height: variantImageHeight,
          alt: variantImageAlt
        });

        // Mark this card as having a selected variant (disables hover)
        card.classList.add('variant-selected');

        // Update active state on swatches
        const allSwatches = card.querySelectorAll('.card__color-swatch');
        allSwatches.forEach(s => s.classList.remove('active'));
        swatch.classList.add('active');

        // Update the product links to include the variant parameter
        this.updateProductLinks(card, variantId);
      });
    }, { passive: false });

    // Also handle hover for instant feedback
    document.addEventListener('mouseenter', (e) => {
      // Defensive check: e.target might not be an Element
      let swatch = null;
      if (e.target && typeof e.target.closest === 'function') {
        swatch = e.target.closest('.card__color-swatch');
      }
      if (!swatch || swatch.classList.contains('active')) return;

      const variantImage = swatch.dataset.variantImage;
      if (variantImage && !this.imageCache.has(variantImage)) {
        this.preloadImage(variantImage);
      }
    }, true);
  }

  preloadImages() {
    // Preload all variant images on the page for instant switching
    const swatches = document.querySelectorAll('.card__color-swatch[data-variant-image]');
    swatches.forEach(swatch => {
      const imageUrl = swatch.dataset.variantImage;
      if (imageUrl) {
        this.preloadImage(imageUrl);
      }
    });
  }

  preloadImage(url) {
    if (this.imageCache.has(url)) return;
    
    const img = new Image();
    img.src = this.getImageUrl(url, 533);
    this.imageCache.set(url, img);
  }

  updateCardImage(card, imageData) {
    const primaryImage = card.querySelector('.card__media .media img:first-child');
    if (!primaryImage) return;

    // Use cached image if available for instant display
    const cachedImage = this.imageCache.get(imageData.url);
    const newSrc = this.getImageUrl(imageData.url, 533);
    
    // Generate srcset for the new image
    const srcset = this.generateSrcset(imageData.url, imageData.width);
    
    // Update all attributes at once to minimize reflows
    primaryImage.srcset = srcset;
    primaryImage.src = newSrc;
    primaryImage.alt = imageData.alt || '';
    primaryImage.width = imageData.width;
    primaryImage.height = imageData.height;
    
    // Force immediate display if cached
    if (cachedImage && cachedImage.complete) {
      primaryImage.style.opacity = '1';
    }
  }

  generateSrcset(baseUrl, maxWidth) {
    const widths = [165, 360, 533, 720, 940, 1066];
    const srcsetParts = [];

    widths.forEach(width => {
      if (maxWidth >= width) {
        srcsetParts.push(`${this.getImageUrl(baseUrl, width)} ${width}w`);
      }
    });

    // Add the full size image
    srcsetParts.push(`${baseUrl} ${maxWidth}w`);

    return srcsetParts.join(', ');
  }

  getImageUrl(baseUrl, width) {
    // Remove any existing width parameter
    const url = baseUrl.split('?')[0];
    return `${url}?width=${width}`;
  }

  updateProductLinks(card, variantId) {
    const links = card.querySelectorAll('a[href*="/products/"]');
    links.forEach(link => {
      const url = new URL(link.href);
      url.searchParams.set('variant', variantId);
      link.href = url.toString();
    });
  }
}

// Initialize immediately for faster response
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CardVariantSwitcher();
  });
} else {
  new CardVariantSwitcher();
}
